import { useEffect, useRef } from 'react';

interface ScrollRevealOptions {
  threshold?: number;
  rootMargin?: string;
  staggerDelay?: number;
}

/**
 * A custom hook to handle scroll reveal animations
 */
export const useScrollReveal = ({
  threshold = 0.1,
  rootMargin = '0px',
  staggerDelay = 150
}: ScrollRevealOptions = {}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentRef = ref.current;
    
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // If the container is a staggered container, animate children with delay
            const staggerItems = currentRef.querySelectorAll('.stagger-item');
            
            if (staggerItems.length) {
              staggerItems.forEach((item, index) => {
                setTimeout(() => {
                  item.classList.add('animate');
                }, index * staggerDelay);
              });
            } else {
              // Otherwise just reveal the container
              currentRef.classList.add('active');
            }
            
            // Unobserve after animation
            observer.unobserve(currentRef);
          }
        });
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, rootMargin, staggerDelay]);

  return ref;
};

export default useScrollReveal;
